import { useCallback, useEffect, useState } from 'react';

import { getCollectionVocabulary } from '../services/tags';
import { normalizeId } from '../utils/ids';
import { reorderByOwner, tagOwnerCollectionId } from '../utils/tags';
import { getSessionKey, onSessionChange, useSessionUserId } from '../utils/session';

/**
 * A COLLECTION'S TAG VOCABULARY (docs/plans/tags.md §5.3): its own tags, then
 * its parent's that the reader may see (`GET /api/tags?collection_id=` lists
 * both, each owner's in tree order — a sub-collection's items may carry
 * their parent's tags; restricted ones only for readers of their own
 * collection, ruled d24). ITEM TAGS are never listed one by one: the same
 * answer carries `itemTagCards`, one row per card that uses its items as tags
 * (T4d — `{ collection_id, card_id, mode, applyWhereReferenced, count }`),
 * kept beside the tags and handed out as `itemTagCards`.
 *
 * Many surfaces ask about one collection at once — every History row naming
 * a tag, the Settings › Tags manager, and (T4b) every chip row — so it is
 * fetched ONCE per collection and shared: the useDuplicateProfiles shape.
 *
 * - SESSION-STAMPED. Every entry carries the session it was fetched under and
 *   is served only to that session; `onSessionChange` clears the whole cache
 *   when another account (or none) signs in on this tab. A collection's
 *   restricted tag names are for its readers alone.
 * - PUBLISHED ON EVERY EDIT. The manager writes, then `publishCollectionTags`
 *   hands every mounted reader of that collection the new list at once; and
 *   `invalidateTagOwner(ownerId)` drops (and re-asks for) every OTHER cached
 *   vocabulary that carries that owner's tags — a sub-collection's list holds
 *   its parent's, so an edit on the parent reaches it too.
 * - STALE after a minute: the next surface to mount re-asks, so another
 *   manager's change arrives on navigation. A failed load is not remembered.
 *
 * `useCollectionTags(collectionId, { enabled })` → `{ tags, itemTagCards,
 * loading, loaded, error, refresh, publish }`.
 */

const EMPTY = Object.freeze([]);
const STALE_MS = 60 * 1000;
// collectionId -> { at, owner, promise, tags, itemTagCards, loaded }
const cache = new Map();
// collectionId -> Set<(entry) => void>
const listeners = new Map();

const sessionKey = getSessionKey;

onSessionChange(() => { cache.clear(); });

function entryFor(id) {
	const hit = cache.get(id);
	if (!hit) return null;
	if (hit.owner === sessionKey()) return hit;
	cache.delete(id);
	return null;
}

function notify(id, payload) {
	(listeners.get(id) || new Set()).forEach(fn => fn(payload));
}

function load(id, { force = false } = {}) {
	const hit = entryFor(id);
	if (!force && hit && hit.loaded && Date.now() - hit.at < STALE_MS) return hit.promise;
	if (!force && hit && hit.pending) return hit.promise;
	const entry = { at: Date.now(), owner: sessionKey(), tags: hit?.tags || EMPTY, itemTagCards: hit?.itemTagCards || EMPTY, loaded: Boolean(hit?.loaded), pending: true, promise: null };
	// What a caller awaiting this load is handed: the answer — unless a
	// publish (a manager's write) or a newer load superseded it meanwhile, and
	// then whatever the cache holds now, so an older read never overwrites a
	// newer list on screen.
	const settle = out => {
		const now = cache.get(id);
		if (now && now !== entry && now.owner === entry.owner && now.loaded) return { tags: now.tags, itemTagCards: now.itemTagCards || EMPTY, error: null };
		return out;
	};
	entry.promise = Promise.resolve()
		.then(() => getCollectionVocabulary(id))
		.then(answer => {
			if (entry.owner !== sessionKey()) {
				if (cache.get(id) === entry) cache.delete(id);
				return { tags: EMPTY, itemTagCards: EMPTY, error: null, foreign: true };
			}
			entry.tags = Object.freeze(reorderByOwner(answer?.tags || []));
			entry.itemTagCards = Object.freeze(Array.isArray(answer?.itemTagCards) ? answer.itemTagCards : []);
			entry.loaded = true;
			entry.pending = false;
			entry.at = Date.now();
			if (cache.get(id) === entry) notify(id, { tags: entry.tags, itemTagCards: entry.itemTagCards, error: null });
			return settle({ tags: entry.tags, itemTagCards: entry.itemTagCards, error: null });
		})
		.catch(error => {
			entry.pending = false;
			if (cache.get(id) === entry) {
				cache.delete(id);
				notify(id, { tags: entry.tags, itemTagCards: entry.itemTagCards, error });
			}
			return settle({ tags: entry.tags, itemTagCards: entry.itemTagCards, error });
		});
	cache.set(id, entry);
	return entry.promise;
}

/**
 * The list a manager just wrote (or its optimistic draft), published: the
 * shared entry becomes it, and every mounted reader of the collection
 * re-renders with it. The list is put back in tree order per owner.
 */
export function publishCollectionTags(collectionId, tags) {
	const id = normalizeId(collectionId);
	if (!id) return;
	const list = Object.freeze(reorderByOwner(Array.isArray(tags) ? tags : []));
	// A tag write leaves the item-tag rows as they were.
	const itemTagCards = entryFor(id)?.itemTagCards || EMPTY;
	cache.set(id, { at: Date.now(), owner: sessionKey(), tags: list, itemTagCards, loaded: true, pending: false, promise: Promise.resolve({ tags: list, itemTagCards, error: null }) });
	notify(id, { tags: list, itemTagCards, error: null });
}

/**
 * Another list carries this owner's tags — a sub-collection's vocabulary
 * holds its parent's. Drop every cached vocabulary (but `except`) that has a
 * tag of `ownerCollectionId`, and have its mounted readers ask again.
 */
export function invalidateTagOwner(ownerCollectionId, { except = null } = {}) {
	const owner = normalizeId(ownerCollectionId);
	const keep = normalizeId(except);
	if (!owner) return;
	[...cache.entries()].forEach(([id, entry]) => {
		if (id === keep) return;
		const carries = id === owner || (entry.tags || []).some(tag => tagOwnerCollectionId(tag) === owner);
		if (!carries) return;
		cache.delete(id);
		if (listeners.get(id)?.size) load(id);
	});
}

/**
 * A card's "Use items as tags" changed in `collectionId` (T4d): drop its
 * cached vocabulary and every other that lists that collection's item-tag
 * rows (a sub-collection's lists its parent's), and have their mounted
 * readers ask again.
 */
export function invalidateItemTagCards(collectionId) {
	const owner = normalizeId(collectionId);
	if (!owner) return;
	[...cache.entries()].forEach(([id, entry]) => {
		const carries = id === owner || (entry.itemTagCards || []).some(row => normalizeId(row?.collection_id) === owner);
		if (!carries) return;
		cache.delete(id);
		if (listeners.get(id)?.size) load(id);
	});
}

/** What the cache holds for a collection now (this session's), or null. */
export function peekCollectionTags(collectionId) {
	const hit = entryFor(normalizeId(collectionId));
	return hit && hit.loaded ? hit.tags : null;
}

/** Test seam: forget every collection. */
export function resetCollectionTagsCache() {
	cache.clear();
	listeners.clear();
}

export default function useCollectionTags(collectionId, { enabled = true } = {}) {
	const id = enabled ? normalizeId(collectionId) || '' : '';
	// Another account signing in with the surface still mounted re-runs the
	// load below (the cache itself was cleared by onSessionChange), and —
	// through `who` in the state — no render ever returns the last account's.
	const who = useSessionUserId();
	const initial = () => {
		const hit = id ? entryFor(id) : null;
		return { id, who, tags: hit?.tags || EMPTY, itemTagCards: hit?.itemTagCards || EMPTY, loaded: Boolean(hit?.loaded), error: null };
	};
	const [state, setState] = useState(initial);

	useEffect(() => {
		if (!id) return undefined;
		let active = true;
		const put = ({ tags, itemTagCards, error, foreign }) => {
			if (!active || foreign) return;
			setState(prev => {
				const same = prev.id === id && prev.who === who;
				// A failed read keeps what was shown; it never blanks a list.
				const loaded = error ? same && prev.loaded : true;
				const nextTags = error ? (same ? prev.tags : EMPTY) : (tags || EMPTY);
				const nextCards = error ? (same ? prev.itemTagCards : EMPTY) : (itemTagCards || EMPTY);
				const nextError = error || null;
				if (same && prev.tags === nextTags && prev.itemTagCards === nextCards && prev.error === nextError && prev.loaded === loaded) return prev;
				return { id, who, tags: nextTags, itemTagCards: nextCards, loaded, error: nextError };
			});
		};
		if (!listeners.has(id)) listeners.set(id, new Set());
		listeners.get(id).add(put);
		const hit = entryFor(id);
		setState(prev => (prev.id === id && prev.who === who ? prev : { id, who, tags: hit?.tags || EMPTY, itemTagCards: hit?.itemTagCards || EMPTY, loaded: Boolean(hit?.loaded), error: null }));
		load(id).then(put);
		return () => {
			active = false;
			listeners.get(id)?.delete(put);
		};
	}, [id, who]);

	const refresh = useCallback(() => (id ? load(id, { force: true }) : Promise.resolve({ tags: EMPTY, itemTagCards: EMPTY, error: null })), [id]);
	const publish = useCallback(list => { if (id) publishCollectionTags(id, list); }, [id]);

	const current = id && state.id === id && state.who === who;
	return {
		tags: current ? state.tags : EMPTY,
		itemTagCards: current ? state.itemTagCards || EMPTY : EMPTY,
		loaded: current ? state.loaded : false,
		loading: Boolean(id) && !(current && (state.loaded || state.error)),
		error: current ? state.error : null,
		refresh,
		publish,
	};
}
